'use client';

import * as LabelPrimitive from '@radix-ui/react-label';
import { Slot } from '@radix-ui/react-slot';
import * as React from 'react';
import {
    Controller,
    ControllerProps,
    FieldPath,
    FieldValues,
    FormProvider,
    UseFormReturn,
    useFormContext,
} from 'react-hook-form';

import { Label } from '@/components/ui/label';
import { useFormAnalytics } from '@/hooks/useFormAnalytics';
import { cn } from '@/lib/utils';

type FormProps<TFieldValues extends FieldValues = FieldValues> = UseFormReturn<TFieldValues> & {
    children: React.ReactNode;
    analyticsTrackingName?: string;
    step?: number | string;
    ignoreFields?: string[];
};

const composeEventHandlers = <E extends React.SyntheticEvent>(
    ours?: (event: E) => void,
    theirs?: (event: E) => void
) => {
    return (event: E) => {
        ours?.(event);
        theirs?.(event);
    };
};

function Form<TFieldValues extends FieldValues = FieldValues>({
    children,
    analyticsTrackingName,
    step,
    ignoreFields,
    ...methods
}: FormProps<TFieldValues>) {
    const { onFirstFocus } = useFormAnalytics<TFieldValues>({
        formName: analyticsTrackingName,
        step,
        ignoreFields,
        methods: methods as UseFormReturn<TFieldValues>,
    });

    type EnhancableProps = {
        children?: React.ReactNode;
        onFocusCapture?: React.FocusEventHandler<any>;
    };

    const enhanceChildren = React.useCallback(
        (root: React.ReactNode): React.ReactNode => {
            function enhance(child: React.ReactNode): React.ReactNode {
                if (!React.isValidElement<EnhancableProps>(child)) return child;

                const nextProps: EnhancableProps = {};

                if (child.props.children != null) {
                    nextProps.children = React.Children.map(child.props.children, enhance);
                }

                if (child.type === 'form') {
                    nextProps.onFocusCapture = composeEventHandlers(onFirstFocus, child.props.onFocusCapture);
                }

                return React.cloneElement(child, nextProps);
            }

            return enhance(root);
        },
        [onFirstFocus]
    );

    const content = React.useMemo(() => React.Children.map(children, enhanceChildren), [children, enhanceChildren]);

    return <FormProvider {...(methods as UseFormReturn<TFieldValues>)}>{content}</FormProvider>;
}

type FormFieldContextValue<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
    name: TName;
};

const FormFieldContext = React.createContext<FormFieldContextValue>({} as FormFieldContextValue);

const FormField = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
    ...props
}: ControllerProps<TFieldValues, TName>) => {
    return (
        <FormFieldContext.Provider value={{ name: props.name }}>
            <Controller {...props} />
        </FormFieldContext.Provider>
    );
};

const useFormField = () => {
    const fieldContext = React.useContext(FormFieldContext);
    const itemContext = React.useContext(FormItemContext);
    const { getFieldState, formState } = useFormContext();

    const fieldState = getFieldState(fieldContext.name, formState);

    if (!fieldContext) {
        throw new Error('useFormField should be used within <FormField>');
    }

    const { id } = itemContext;

    return {
        id,
        name: fieldContext.name,
        formItemId: `${id}-form-item`,
        formDescriptionId: `${id}-form-item-description`,
        formMessageId: `${id}-form-item-message`,
        ...fieldState,
    };
};

type FormItemContextValue = {
    id: string;
};

const FormItemContext = React.createContext<FormItemContextValue>({} as FormItemContextValue);

type FormItemProps = React.HTMLAttributes<HTMLDivElement> & {
    noWrapping?: boolean;
};

const FormItem = React.forwardRef<HTMLDivElement, FormItemProps>(
    ({ className, noWrapping = false, children, ...props }, ref) => {
        const id = React.useId();

        return (
            <FormItemContext.Provider value={{ id }}>
                {noWrapping ? (
                    <>{children}</>
                ) : (
                    <div ref={ref} className={cn('space-y-2', className)} {...props}>
                        {children}
                    </div>
                )}
            </FormItemContext.Provider>
        );
    }
);

FormItem.displayName = 'FormItem';

const FormLabel = React.forwardRef<
    React.ElementRef<typeof LabelPrimitive.Root>,
    React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>
>(({ className, ...props }, ref) => {
    const { error, formItemId } = useFormField();

    return <Label ref={ref} className={cn(error && 'text-destructive', className)} htmlFor={formItemId} {...props} />;
});
FormLabel.displayName = 'FormLabel';

const FormControl = React.forwardRef<React.ElementRef<typeof Slot>, React.ComponentPropsWithoutRef<typeof Slot>>(
    ({ ...props }, ref) => {
        const { error, formItemId, formDescriptionId, formMessageId } = useFormField();

        return (
            <Slot
                ref={ref}
                id={formItemId}
                aria-describedby={!error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`}
                aria-invalid={!!error}
                {...props}
            />
        );
    }
);
FormControl.displayName = 'FormControl';

const FormDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
    ({ className, ...props }, ref) => {
        const { formDescriptionId } = useFormField();

        return (
            <p ref={ref} id={formDescriptionId} className={cn('text-sm text-muted-foreground', className)} {...props} />
        );
    }
);
FormDescription.displayName = 'FormDescription';

const FormMessage = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
    ({ className, children, ...props }, ref) => {
        const { error, formMessageId } = useFormField();
        const body = error ? String(error?.message) : children;

        if (!body) {
            return null;
        }

        return (
            <p
                ref={ref}
                id={formMessageId}
                className={cn('text-xs font-medium text-destructive', className)}
                {...props}
            >
                {body}
            </p>
        );
    }
);
FormMessage.displayName = 'FormMessage';

export { useFormField, Form, FormItem, FormLabel, FormControl, FormDescription, FormMessage, FormField };
